import { createContext, useContext, useState, useEffect, useMemo, useRef, type ReactNode } from 'react'
import type { ContactEntity } from '@common/models/contact/contact.modl'
import type { SchoolAccount } from '@common/models/account/school/school-account.modl'
import { ContactService } from '@common/services/contact-service'
import { fetchActiveSchoolAccountsByIds } from '@/services/school-account-lookup'
import { useAuth } from './AuthContext'
import { useAdminAccount } from './AdminAccountContext'

export type ContactWithSchool = ContactEntity & {
  schoolAccountData: SchoolAccount | null;
};

type ContactContextType = {
  contacts: ContactEntity[];
  contactsWithSchool: ContactWithSchool[];
  isLoading: boolean;
  error: Error | null;
};

const ContactContext = createContext<ContactContextType>({
  contacts: [],
  contactsWithSchool: [],
  isLoading: true,
  error: null,
})

export const ContactProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth()
  const { adminAccount, isLoading: adminLoading } = useAdminAccount()
  const [contacts, setContacts] = useState<ContactEntity[]>([])
  const [schoolAccountMap, setSchoolAccountMap] = useState<Map<string, SchoolAccount>>(new Map())
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const indexErrorLoggedRef = useRef(false);
  const loadingSchoolIdsRef = useRef<Set<string>>(new Set())

  const canSubscribeContacts = Boolean(user && adminAccount)

  // Contactに学校情報を付与
  const contactsWithSchool = useMemo(() => {
    return contacts.map((contact) => {
      const schoolAccountId = contact.school_account_id ?? null
      const schoolAccountData = schoolAccountId ? schoolAccountMap.get(schoolAccountId) || null : null
      
      return {
        ...contact,
        schoolAccountData,
      } as ContactWithSchool;
    });
  }, [contacts, schoolAccountMap]);

  useEffect(() => {
    if (!canSubscribeContacts) {
      setContacts([])
      setSchoolAccountMap(new Map())
      setIsLoading(adminLoading)
      setError(null)
      return
    }

    setIsLoading(true)
    setError(null)
    indexErrorLoggedRef.current = false

    const service = new ContactService()
    const unsubscribe = service.subscribeActiveForAdmin(
      (fetchedContacts) => {
        setContacts(fetchedContacts)
        setIsLoading(false)
      },
      (err) => {
        const isIndexError = String(err).includes('index') || (err as { code?: string })?.code === 'failed-precondition'
        if (!indexErrorLoggedRef.current && isIndexError) {
          indexErrorLoggedRef.current = true
          console.error('Failed to subscribe contacts (index required). Create the index from the URL in the error below:', err)
        } else if (!isIndexError) {
          console.error('Failed to subscribe contacts:', err)
        }
        setError(err instanceof Error ? err : new Error('お問い合わせの取得に失敗しました'))
        setIsLoading(false)
      },
    )

    return () => unsubscribe()
  }, [canSubscribeContacts, adminLoading])

  useEffect(() => {
    const schoolIds = Array.from(
      new Set(
        contacts
          .map((contact) => contact.school_account_id)
          .filter((id): id is string => Boolean(id)),
      ),
    )

    if (schoolIds.length === 0) {
      loadingSchoolIdsRef.current.clear()
      setSchoolAccountMap((prev) => (prev.size === 0 ? prev : new Map()))
      return
    }

    // すでに不要になった学校情報は削除してメモリを抑える
    setSchoolAccountMap((prev) => {
      let changed = false
      const next = new Map<string, SchoolAccount>()
      schoolIds.forEach((id) => {
        const account = prev.get(id)
        if (account) next.set(id, account)
      })
      if (next.size !== prev.size) {
        changed = true
      } else {
        for (const id of prev.keys()) {
          if (!next.has(id)) {
            changed = true
            break
          }
        }
      }
      if (!changed) return prev
      return next
    })

    const missingIds = schoolIds.filter(
      (id) => !schoolAccountMap.has(id) && !loadingSchoolIdsRef.current.has(id),
    )
    if (missingIds.length === 0) return

    missingIds.forEach((id) => loadingSchoolIdsRef.current.add(id))

    let cancelled = false
    const fetchSchoolAccounts = async () => {
      try {
        const fetchedMap = await fetchActiveSchoolAccountsByIds(missingIds)

        if (cancelled) return
        setSchoolAccountMap((prev) => {
          const next = new Map(prev)
          fetchedMap.forEach((value, key) => next.set(key, value))
          return next
        })
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch school accounts for contacts:', err)
          setError(err instanceof Error ? err : new Error('学校情報の取得に失敗しました'))
        }
      } finally {
        missingIds.forEach((id) => loadingSchoolIdsRef.current.delete(id))
      }
    }

    fetchSchoolAccounts()

    return () => {
      cancelled = true
    }
  }, [contacts, schoolAccountMap]);

  return (
    <ContactContext.Provider value={{ contacts, contactsWithSchool, isLoading, error }}>
      {children}
    </ContactContext.Provider>
  );
};

export const useContact = () => {
  const context = useContext(ContactContext);
  if (!context) {
    throw new Error('useContact must be used within a ContactProvider');
  }
  return context;
};
